import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, Printer, Briefcase, Ruler, IndianRupee, TrendingUp, Percent,
  Activity, CheckCircle2, Loader, CircleDashed, AlertTriangle,
  Route, Spline, Building2, SlidersHorizontal, X, Lock,
} from 'lucide-react';
import { dashboardApi } from '../../services/dashboard-api.service';

const GRANTS = ['All', 'CRR', 'MRR', 'SDF', 'FDR', 'PMGSY', 'MGNREGS', 'DMFT', 'IMS'];
const FYS = ['2026-27', '2025-26', '2024-25', '2023-24'];
const GRANT_LEVELS = ['State', 'Central', 'District'];
const ABSTRACT_STAGES = ['Not Started', 'In Progress', 'Completed', 'Held Up'];
const CATEGORIES = ['P', 'S', 'T'];

// PR Jurisdiction cascade — same hierarchy master endpoints the Works filters use.
const API_BASE = process.env.REACT_APP_API_URL || 'https://predworks.alliantprojects.com/api/v1';
const unwrapList = (j: any): any[] => (Array.isArray(j) ? j : j?.data ?? j?.items ?? []);
async function fetchHierarchy(path: string): Promise<any[]> {
  try {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    const res = await fetch(`${API_BASE}${path}`, { headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
    if (!res.ok) return [];
    return unwrapList(await res.json());
  } catch { return []; }
}
const JUR_ORDER = ['territoryId', 'circleId', 'divisionId', 'subDivisionId', 'mandalId'] as const;
type JurKey = typeof JUR_ORDER[number];

const cr = (v: number) => (v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const km = (v: number) => (v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (v: number) => (v ?? 0).toLocaleString('en-IN');
const pc = (v: number) => `${(v ?? 0).toFixed(2)}%`;

// ===== KPI card =====
const KPI: React.FC<{ icon: React.ReactNode; label: string; value: string; sub?: string; color: string }> = ({ icon, label, value, sub, color }) => (
  <div className="bg-white rounded-lg border border-gray-200 shadow-sm px-2.5 py-2 flex items-center gap-2 min-w-0 overflow-hidden">
    <div className="rounded-lg p-1.5 shrink-0" style={{ backgroundColor: `${color}1a`, color }}>{icon}</div>
    <div className="min-w-0 flex-1">
      <div className="text-[9px] font-semibold uppercase tracking-wide truncate" style={{ color }} title={label}>{label}</div>
      <div className="text-base font-bold leading-tight truncate tabular-nums" style={{ color }} title={value}>{value}</div>
      {sub && <div className="text-[8px] text-gray-400 leading-none truncate">{sub}</div>}
    </div>
  </div>
);

// ===== Panel wrapper =====
const Panel: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({ title, children, className }) => (
  <div className={`bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col ${className || ''}`}>
    <div className="bg-[#1f6fc4] text-white px-3 py-1.5 text-center text-xs font-bold uppercase tracking-wide">{title}</div>
    <div className="p-2 flex-1">{children}</div>
  </div>
);

// ===== Circular gauge =====
const Gauge: React.FC<{ value: number; color: string; label: string }> = ({ value, color, label }) => {
  const r = 46, c = 2 * Math.PI * r, off = c * (1 - Math.min(value, 100) / 100);
  return (
    <div className="flex flex-col items-center">
      <svg width="120" height="120" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#e5edf6" strokeWidth="12" />
        <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 60 60)" />
        <text x="60" y="64" textAnchor="middle" fontSize="22" fontWeight="700" fill={color}>{value.toFixed(2)}%</text>
      </svg>
      <div className="text-xs font-semibold text-gray-600 -mt-1">{label}</div>
    </div>
  );
};

// ===== Donut =====
const DONUT_COLORS = ['#2f6fb3', '#e8852f', '#2fae5f', '#d9433f', '#7c5cc4'];
// Map a donut slice label back to the backend asset filter key.
const assetKey = (label: string) => (label.startsWith('Buildings') ? 'Buildings' : label);
const Donut: React.FC<{ data: { asset: string; works: number; pct: number }[]; total: number; active?: string; onSelect?: (key: string) => void }> = ({ data, total, active, onSelect }) => {
  const R = 52, sw = 26, c = 2 * Math.PI * R;
  let acc = 0;
  return (
    <div className="flex items-center gap-3">
      <svg width="150" height="150" viewBox="0 0 150 150" className="shrink-0">
        {data.map((d, i) => {
          const frac = total ? d.works / total : 0;
          const dash = `${frac * c} ${c}`;
          const isActive = active === assetKey(d.asset);
          const el = (
            <circle key={i} cx="75" cy="75" r={R} fill="none" stroke={DONUT_COLORS[i % DONUT_COLORS.length]}
              strokeWidth={isActive ? sw + 6 : sw} strokeDasharray={dash} strokeDashoffset={-acc * c} transform="rotate(-90 75 75)"
              className={onSelect ? 'cursor-pointer' : ''} style={{ opacity: active && !isActive ? 0.4 : 1 }}
              onClick={onSelect ? () => onSelect(assetKey(d.asset)) : undefined}>
              <title>{d.asset}: {num(d.works)} ({pc(d.pct)})</title>
            </circle>
          );
          acc += frac;
          return el;
        })}
        <text x="75" y="70" textAnchor="middle" fontSize="11" fill="#64748b">Total Works</text>
        <text x="75" y="88" textAnchor="middle" fontSize="20" fontWeight="700" fill="#1e293b">{num(total)}</text>
      </svg>
      <div className="space-y-1 text-xs">
        {data.map((d, i) => {
          const isActive = active === assetKey(d.asset);
          return (
            <div key={i}
              className={`flex items-center gap-1.5 rounded px-1 -mx-1 ${onSelect ? 'cursor-pointer hover:bg-gray-50' : ''} ${isActive ? 'bg-blue-50 ring-1 ring-blue-200' : ''}`}
              onClick={onSelect ? () => onSelect(assetKey(d.asset)) : undefined}
              title={`Filter dashboard by ${d.asset}`}>
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
              <span className="text-gray-600">{d.asset}</span>
              <span className="font-semibold text-gray-800 ml-auto">{num(d.works)}</span>
              <span className="text-gray-400">({pc(d.pct)})</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ===== Horizontal % bar list =====
const BarList: React.FC<{ data: { label: string; value: number }[]; color: string; max?: number }> = ({ data, color, max = 100 }) => (
  <div className="space-y-1.5">
    {data.map((d, i) => (
      <div key={i} className="flex items-center gap-2 text-xs">
        <span className="w-20 truncate text-gray-600 text-right">{d.label}</span>
        <div className="flex-1 bg-gray-100 rounded h-3.5 relative overflow-hidden">
          <div className="h-full rounded" style={{ width: `${Math.min((d.value / max) * 100, 100)}%`, backgroundColor: color }} />
        </div>
        <span className="w-12 font-semibold text-gray-700">{pc(d.value)}</span>
      </div>
    ))}
  </div>
);

// One asset track in the Summary (%) panel: icon + label on the left, then a grid
// of stage columns with a "Works (Nos)" row and a "% of Total X Works" row that
// carries a horizontal bar + percentage. Matches the department screenshot.
const StageTrack: React.FC<{ asset: string; icon: React.ReactNode; list: { stage: string; count: number }[]; color: string }> = ({ asset, icon, list, color }) => {
  const tot = list.reduce((s, x) => s + x.count, 0) || 1;
  const label = asset === 'Buildings' ? 'BUILDINGS (IMS etc.)' : asset.toUpperCase();
  return (
    <div className="flex items-center gap-2 border-t border-gray-100 pt-2 first:border-t-0 first:pt-0">
      <div className="flex flex-col items-center w-14 shrink-0 text-center">
        <div className="rounded-lg p-1.5" style={{ backgroundColor: `${color}1a`, color }}>{icon}</div>
        <div className="text-[9px] font-bold mt-1 leading-tight" style={{ color }}>{label}</div>
      </div>
      <div className="flex-1 overflow-x-auto">
        <table className="w-full text-[8px] border-collapse">
          <thead>
            <tr className="text-gray-600">
              <th className="text-left font-medium px-1 py-0.5 whitespace-nowrap w-28">Stage</th>
              {list.map((s, i) => <th key={i} className="px-1 py-0.5 text-center font-semibold whitespace-nowrap">{s.stage}</th>)}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="text-left text-gray-500 px-1 py-0.5 whitespace-nowrap">Works (Nos)</td>
              {list.map((s, i) => <td key={i} className="px-1 py-0.5 text-center font-semibold text-gray-700">{num(s.count)}</td>)}
            </tr>
            <tr>
              <td className="text-left text-gray-500 px-1 py-0.5 whitespace-nowrap">% of Total {asset} Works</td>
              {list.map((s, i) => {
                const p = (s.count / tot) * 100;
                return (
                  <td key={i} className="px-1 py-0.5 align-bottom">
                    <div className="h-2 bg-gray-100 rounded overflow-hidden"><div className="h-full rounded" style={{ width: `${p}%`, backgroundColor: color }} /></div>
                    <div className="text-center text-[7px] font-medium text-gray-600 mt-0.5">{p.toFixed(2)}%</div>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SEG = { total: '#1f6fc4', completed: '#16a34a', in_progress: '#ea8a1e', not_started: '#dc2626' };

const StateLevelDashboard: React.FC = () => {
  const [grant, setGrant] = useState('All');
  const [fy, setFy] = useState('2026-27');
  const [asset, setAsset] = useState<string>(''); // donut click filter
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Extended ("previous dashboard") filters — collapsible More Filters panel.
  const [adv, setAdv] = useState<Record<string, any>>({});
  const [showMore, setShowMore] = useState(false);
  const [opts, setOpts] = useState<{ workTypes: string[]; subStages: string[]; contractors: { id: number; name: string }[]; constituencies: { id: number; name: string }[] }>({ workTypes: [], subStages: [], contractors: [], constituencies: [] });
  const setAdvF = (key: string, value: any) => setAdv((p) => {
    const n = { ...p };
    if (value === '' || value == null) delete n[key]; else n[key] = value;
    return n;
  });
  useEffect(() => {
    (async () => {
      const [wt, ss, co, cons] = await Promise.all([
        fetchHierarchy('/master-data/work-types'), fetchHierarchy('/work-stages/level/SUB'),
        fetchHierarchy('/master-data/contractors'), fetchHierarchy('/constituencies'),
      ]);
      setOpts({
        workTypes: wt.map((w: any) => w.work_type_name).filter(Boolean),
        subStages: ss.map((s: any) => s.name).filter(Boolean),
        contractors: co.map((c: any) => ({ id: c.contractor_id || c.id, name: c.contractor_name || c.firm_name || c.name })).filter((c: any) => c.id && c.name),
        constituencies: cons.map((c: any) => ({ id: c.id, name: c.name })).filter((c: any) => c.id && c.name),
      });
    })();
  }, []);

  // PR Jurisdiction cascade (Territory → Circle → Division → Sub-division → Mandal).
  const [jur, setJur] = useState<Record<JurKey, string>>({ territoryId: '', circleId: '', divisionId: '', subDivisionId: '', mandalId: '' });
  const [territories, setTerritories] = useState<any[]>([]);
  const [circles, setCircles] = useState<any[]>([]);
  const [divisions, setDivisions] = useState<any[]>([]);
  const [subDivisions, setSubDivisions] = useState<any[]>([]);
  const [mandals, setMandals] = useState<any[]>([]);

  // Set one level and clear all descendant levels.
  const setLevel = (key: JurKey, value: string) => setJur((prev) => {
    const next = { ...prev, [key]: value };
    for (let i = JUR_ORDER.indexOf(key) + 1; i < JUR_ORDER.length; i++) next[JUR_ORDER[i]] = '';
    return next;
  });

  useEffect(() => { fetchHierarchy('/ce-territories/active').then(setTerritories); fetchHierarchy('/divisions?limit=1000').then(setDivisions); }, []);
  // Logged-in user's jurisdiction — used to LOCK the geo filters to their scope.
  const [scope, setScope] = useState<{ isState: boolean; level: string; name: string | null } | null>(null);
  useEffect(() => { dashboardApi.getMyScope().then(setScope).catch(() => setScope({ isState: true, level: 'STATE', name: null })); }, []);
  useEffect(() => { jur.territoryId ? fetchHierarchy(`/circles/ce-territory/${jur.territoryId}`).then(setCircles) : setCircles([]); }, [jur.territoryId]);
  useEffect(() => { jur.divisionId ? fetchHierarchy(`/sub-divisions/division/${jur.divisionId}`).then(setSubDivisions) : setSubDivisions([]); }, [jur.divisionId]);
  useEffect(() => { jur.subDivisionId ? fetchHierarchy(`/mandals/sub-division/${jur.subDivisionId}`).then(setMandals) : setMandals([]); }, [jur.subDivisionId]);
  const divisionOptions = jur.circleId ? divisions.filter((d) => Number(d.circle_id) === Number(jur.circleId)) : divisions;

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const res = await dashboardApi.getStateLevelDashboard({
        grant: grant === 'All' ? undefined : grant, financialYear: fy, asset: asset || undefined,
        territoryId: jur.territoryId || undefined, circleId: jur.circleId || undefined,
        divisionId: jur.divisionId || undefined, subDivisionId: jur.subDivisionId || undefined, mandalId: jur.mandalId || undefined,
        ...adv,
      });
      setData(res);
    } catch (e: any) { setError(e?.message || 'Failed to load dashboard'); }
    finally { setLoading(false); }
  }, [grant, fy, jur, asset, adv]);
  useEffect(() => { load(); }, [load]);

  const k = data?.kpis || {};
  const po = data?.progress_overview || {};
  const assetSummary = data?.asset_summary || [];
  const grants = data?.grant_summary || [];
  const ason = data?.as_on_date || new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-[#e9f1f9] text-[#1e293b]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#15539f] to-[#2f86d4] text-white px-4 py-2.5 flex items-center justify-between">
        <div className="w-32" />
        <h1 className="text-base md:text-lg font-bold tracking-wide text-center flex-1">PRED WORKS &ndash; STATE LEVEL ABSTRACT STATUS &amp; FINANCIAL PROGRESS DASHBOARD</h1>
        <div className="w-32 text-right text-[10px] opacity-90">As on: {ason}</div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border-b border-gray-200 px-3 py-2 flex flex-wrap items-center gap-2 text-xs">
        {(!scope || scope.isState) && [['State', 'All State'], ['Constituency', 'All']].map(([lbl, opt]: any) => (
          <label key={lbl} className="flex items-center gap-1">
            <span className="text-gray-500 font-medium">{lbl}:</span>
            <select className="border border-gray-300 rounded px-2 py-1 bg-white text-gray-700"><option>{opt}</option></select>
          </label>
        ))}
        {/* PR Jurisdiction — locked to the user's scope for non-state users. */}
        {scope && !scope.isState ? (
          <span className="flex items-center gap-1 bg-blue-50 border border-blue-200 rounded px-2 py-1 text-blue-800"
            title="Your view is limited to your assigned jurisdiction">
            <span className="font-medium">PR Jurisdiction:</span>
            {scope.name || scope.level}
            <span className="text-blue-400">({scope.level.replace('_', ' ').toLowerCase()})</span>
            <Lock className="h-3 w-3" />
          </span>
        ) : (
          <>
            <label className="flex items-center gap-1">
              <span className="text-gray-500 font-medium">PR Jurisdiction:</span>
              <select value={jur.territoryId} onChange={e => setLevel('territoryId', e.target.value)} className="border border-gray-300 rounded px-2 py-1 bg-white text-gray-700">
                <option value="">All</option>
                {territories.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            {jur.territoryId && (
              <select value={jur.circleId} onChange={e => setLevel('circleId', e.target.value)} className="border border-gray-300 rounded px-2 py-1 bg-white text-gray-700">
                <option value="">All Circles</option>
                {circles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            {jur.circleId && (
              <select value={jur.divisionId} onChange={e => setLevel('divisionId', e.target.value)} className="border border-gray-300 rounded px-2 py-1 bg-white text-gray-700">
                <option value="">All Divisions</option>
                {divisionOptions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
            {jur.divisionId && (
              <select value={jur.subDivisionId} onChange={e => setLevel('subDivisionId', e.target.value)} className="border border-gray-300 rounded px-2 py-1 bg-white text-gray-700">
                <option value="">All Sub-divisions</option>
                {subDivisions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
            {jur.subDivisionId && (
              <select value={jur.mandalId} onChange={e => setLevel('mandalId', e.target.value)} className="border border-gray-300 rounded px-2 py-1 bg-white text-gray-700">
                <option value="">All Mandals</option>
                {mandals.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            )}
          </>
        )}
        <label className="flex items-center gap-1">
          <span className="text-gray-500 font-medium">Financial Year:</span>
          <select value={fy} onChange={e => setFy(e.target.value)} className="border border-gray-300 rounded px-2 py-1 bg-white text-gray-700">
            {FYS.map(f => <option key={f}>{f}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1">
          <span className="text-gray-500 font-medium">Grant:</span>
          <select value={grant} onChange={e => setGrant(e.target.value)} className="border border-gray-300 rounded px-2 py-1 bg-white text-gray-700">
            {GRANTS.map(g => <option key={g}>{g}</option>)}
          </select>
        </label>
        <button onClick={load} className="ml-auto flex items-center gap-1 bg-[#1f6fc4] text-white rounded px-3 py-1 hover:bg-[#1a5ca5]">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
        <button onClick={() => setShowMore((v) => !v)} className={`flex items-center gap-1 border rounded px-3 py-1 ${Object.keys(adv).length ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 hover:bg-gray-50'}`}>
          <SlidersHorizontal className="h-3.5 w-3.5" /> More Filters{Object.keys(adv).length ? ` (${Object.keys(adv).length})` : ''}
        </button>
        <button onClick={() => window.print()} className="flex items-center gap-1 border border-gray-300 rounded px-3 py-1 hover:bg-gray-50">
          <Printer className="h-3.5 w-3.5" /> Print
        </button>
      </div>

      {/* Extended ("previous dashboard") filters */}
      {showMore && (
        <div className="bg-gray-50 border-b border-gray-200 px-3 py-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 text-xs">
            <label className="flex flex-col gap-0.5"><span className="text-gray-500">Grant Level</span>
              <select className="border border-gray-300 rounded px-2 py-1 bg-white" value={adv.grantLevel || ''} onChange={e => setAdvF('grantLevel', e.target.value)}>
                <option value="">All</option>{GRANT_LEVELS.map(g => <option key={g} value={g}>{g}</option>)}
              </select></label>
            <label className="flex flex-col gap-0.5"><span className="text-gray-500">Abstract Stage</span>
              <select className="border border-gray-300 rounded px-2 py-1 bg-white" value={adv.stage || ''} onChange={e => setAdvF('stage', e.target.value)}>
                <option value="">All</option>{ABSTRACT_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
              </select></label>
            <label className="flex flex-col gap-0.5"><span className="text-gray-500">Sub Stage</span>
              <select className="border border-gray-300 rounded px-2 py-1 bg-white" value={adv.subStage || ''} onChange={e => setAdvF('subStage', e.target.value)}>
                <option value="">All</option>{opts.subStages.map(s => <option key={s} value={s}>{s}</option>)}
              </select></label>
            <label className="flex flex-col gap-0.5"><span className="text-gray-500">Category (P/S/T)</span>
              <select className="border border-gray-300 rounded px-2 py-1 bg-white" value={adv.category || ''} onChange={e => setAdvF('category', e.target.value)}>
                <option value="">All</option>{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select></label>
            <label className="flex flex-col gap-0.5"><span className="text-gray-500">Work Type</span>
              <select className="border border-gray-300 rounded px-2 py-1 bg-white" value={adv.workType || ''} onChange={e => setAdvF('workType', e.target.value)}>
                <option value="">All</option>{opts.workTypes.map(w => <option key={w} value={w}>{w}</option>)}
              </select></label>
            <label className="flex flex-col gap-0.5"><span className="text-gray-500">Contractor</span>
              <select className="border border-gray-300 rounded px-2 py-1 bg-white" value={adv.contractorId || ''} onChange={e => setAdvF('contractorId', e.target.value ? Number(e.target.value) : '')}>
                <option value="">All</option>{opts.contractors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></label>
            {(!scope || scope.isState) && (
              <label className="flex flex-col gap-0.5"><span className="text-gray-500">Constituency</span>
                <select className="border border-gray-300 rounded px-2 py-1 bg-white" value={adv.constituencyId || ''} onChange={e => setAdvF('constituencyId', e.target.value ? Number(e.target.value) : '')}>
                  <option value="">All</option>{opts.constituencies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></label>
            )}
            <label className="flex flex-col gap-0.5"><span className="text-gray-500">Agency</span>
              <input className="border border-gray-300 rounded px-2 py-1 bg-white" placeholder="contains…" value={adv.agencyName || ''} onChange={e => setAdvF('agencyName', e.target.value)} /></label>
            <label className="flex flex-col gap-0.5"><span className="text-gray-500">Min Cost (lakh)</span>
              <input type="number" className="border border-gray-300 rounded px-2 py-1 bg-white" value={adv.minCost ?? ''} onChange={e => setAdvF('minCost', e.target.value ? Number(e.target.value) : '')} /></label>
            <label className="flex flex-col gap-0.5"><span className="text-gray-500">Max Cost (lakh)</span>
              <input type="number" className="border border-gray-300 rounded px-2 py-1 bg-white" value={adv.maxCost ?? ''} onChange={e => setAdvF('maxCost', e.target.value ? Number(e.target.value) : '')} /></label>
            <label className="flex flex-col gap-0.5"><span className="text-gray-500">Sanction From</span>
              <input type="date" className="border border-gray-300 rounded px-2 py-1 bg-white" value={adv.fromDate || ''} onChange={e => setAdvF('fromDate', e.target.value)} /></label>
            <label className="flex flex-col gap-0.5"><span className="text-gray-500">Search</span>
              <input className="border border-gray-300 rounded px-2 py-1 bg-white" placeholder="work / agency / proc no." value={adv.searchTerm || ''} onChange={e => setAdvF('searchTerm', e.target.value)} /></label>
          </div>
          {Object.keys(adv).length > 0 && (
            <button onClick={() => setAdv({})} className="mt-2 inline-flex items-center gap-1 text-[11px] text-red-600 hover:text-red-800">
              <X className="h-3 w-3" /> Clear extended filters
            </button>
          )}
        </div>
      )}

      {error && <div className="m-3 bg-red-50 border border-red-200 text-red-800 text-sm rounded px-3 py-2">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-24 text-gray-500"><RefreshCw className="h-7 w-7 animate-spin mr-2" /> Loading dashboard…</div>
      ) : data && (
        <div className="p-3 space-y-3">
          {/* KPI strip */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 xl:grid-cols-10 gap-2">
            <KPI color="#2563eb" icon={<Briefcase className="h-4 w-4" />} label="Total Works" value={num(k.total_works)} />
            <KPI color="#0d9488" icon={<Ruler className="h-4 w-4" />} label="Total Length" value={km(k.total_length_km)} />
            <KPI color="#7c3aed" icon={<IndianRupee className="h-4 w-4" />} label="Total Est. Cost (₹ Cr)" value={cr(k.total_est_cost_cr)} />
            <KPI color="#ea580c" icon={<TrendingUp className="h-4 w-4" />} label="Expenditure (₹ Cr)" value={cr(k.expenditure_cr)} />
            <KPI color="#dc2626" icon={<Percent className="h-4 w-4" />} label="Financial Progress" value={pc(k.financial_progress_pct)} sub="(Exp./Est. Cost)" />
            <KPI color="#0891b2" icon={<Activity className="h-4 w-4" />} label="Physical Progress" value={pc(k.physical_progress_pct)} />
            <KPI color="#16a34a" icon={<CheckCircle2 className="h-4 w-4" />} label="Completed Works" value={num(k.completed)} />
            <KPI color="#f59e0b" icon={<Loader className="h-4 w-4" />} label="In Progress" value={num(k.in_progress)} />
            <KPI color="#ef4444" icon={<CircleDashed className="h-4 w-4" />} label="Not Started" value={num(k.not_started)} />
            <KPI color="#d97706" icon={<AlertTriangle className="h-4 w-4" />} label="Hold-Up Works" value={num(k.hold_up)} />
          </div>

          {/* Row: State summary + progress overview */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Panel title="State Level Summary — All Grants (Combined) — including Roads, Bridges & Buildings" className="lg:col-span-2">
              <div className="overflow-x-auto">
                <table className="w-full text-[11px] border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th rowSpan={2} className="border px-2 py-1 text-left">Particulars</th>
                      <th colSpan={3} className="border px-2 py-1 text-center leading-tight" style={{ color: SEG.total }}>Total Work Load<br /><span className="text-[8px] font-normal">(Spillover + New Sanctions)</span></th>
                      <th colSpan={3} className="border px-2 py-1 text-center" style={{ color: SEG.completed }}>Completed</th>
                      <th colSpan={3} className="border px-2 py-1 text-center" style={{ color: SEG.in_progress }}>In Progress</th>
                      <th colSpan={3} className="border px-2 py-1 text-center" style={{ color: SEG.not_started }}>Not Started</th>
                      <th rowSpan={2} className="border px-1 py-1 text-center">Fin %</th>
                      <th rowSpan={2} className="border px-1 py-1 text-center">Phys %</th>
                    </tr>
                    <tr className="bg-gray-50 text-gray-500">
                      {['Works', 'Length', 'Est.Cost', 'Works', 'Length', 'Exp.', 'Works', 'Length', 'Est.Cost', 'Works', 'Length', 'Est.Cost'].map((h, i) =>
                        <th key={i} className="border px-1 py-0.5 text-center font-medium">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {assetSummary.map((a: any) => (
                      <tr key={a.asset} className="text-center">
                        <td className="border px-2 py-1 text-left font-medium">{a.asset}</td>
                        <td className="border px-1 py-1" style={{ color: SEG.total }}>{num(a.total.works)}</td><td className="border px-1 py-1" style={{ color: SEG.total }}>{km(a.total.length_km)}</td><td className="border px-1 py-1" style={{ color: SEG.total }}>{cr(a.total.est_cost_cr)}</td>
                        <td className="border px-1 py-1" style={{ color: SEG.completed }}>{num(a.completed.works)}</td><td className="border px-1 py-1" style={{ color: SEG.completed }}>{km(a.completed.length_km)}</td><td className="border px-1 py-1" style={{ color: SEG.completed }}>{cr(a.completed.est_cost_cr)}</td>
                        <td className="border px-1 py-1" style={{ color: SEG.in_progress }}>{num(a.in_progress.works)}</td><td className="border px-1 py-1" style={{ color: SEG.in_progress }}>{km(a.in_progress.length_km)}</td><td className="border px-1 py-1" style={{ color: SEG.in_progress }}>{cr(a.in_progress.est_cost_cr)}</td>
                        <td className="border px-1 py-1" style={{ color: SEG.not_started }}>{num(a.not_started.works)}</td><td className="border px-1 py-1" style={{ color: SEG.not_started }}>{km(a.not_started.length_km)}</td><td className="border px-1 py-1" style={{ color: SEG.not_started }}>{cr(a.not_started.est_cost_cr)}</td>
                        <td className="border px-1 py-1 font-semibold">{pc(a.financial_pct)}</td><td className="border px-1 py-1 font-semibold">{pc(a.physical_pct)}</td>
                      </tr>
                    ))}
                    <tr className="text-center font-bold bg-gray-50 border-t-2 border-gray-300">
                      <td className="border px-2 py-1 text-left text-red-700">TOTAL (STATE)</td>
                      <td className="border px-1 py-1" style={{ color: SEG.total }}>{num(k.total_works)}</td><td className="border px-1 py-1" style={{ color: SEG.total }}>{km(k.total_length_km)}</td><td className="border px-1 py-1" style={{ color: SEG.total }}>{cr(k.total_est_cost_cr)}</td>
                      <td className="border px-1 py-1" style={{ color: SEG.completed }}>{num(k.completed)}</td><td className="border px-1 py-1" colSpan={2}></td>
                      <td className="border px-1 py-1" style={{ color: SEG.in_progress }}>{num(k.in_progress)}</td><td className="border px-1 py-1" colSpan={2}></td>
                      <td className="border px-1 py-1" style={{ color: SEG.not_started }}>{num(k.not_started)}</td><td className="border px-1 py-1" colSpan={2}></td>
                      <td className="border px-1 py-1">{pc(k.financial_progress_pct)}</td><td className="border px-1 py-1">{pc(k.physical_progress_pct)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Panel>

            <Panel title="State Level Progress Overview">
              <div className="flex justify-around items-center mb-2">
                <Gauge value={po.financial_progress_pct || 0} color="#2563eb" label="Financial Progress" />
                <Gauge value={po.physical_progress_pct || 0} color="#0891b2" label="Physical Progress" />
              </div>
              <div className="text-xs space-y-1 px-1">
                <div className="flex justify-between border-t pt-1"><span className="text-gray-500">Total Estimated Cost</span><span className="font-semibold">₹{cr(po.total_est_cost_cr)} Cr</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Expenditure Incurred</span><span className="font-semibold">₹{cr(po.expenditure_cr)} Cr</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Balance Cost</span><span className="font-semibold">₹{cr(po.balance_cr)} Cr</span></div>
                <div className="flex justify-between"><span className="text-gray-500">% Financial Progress</span><span className="font-semibold text-blue-700">{pc(po.financial_progress_pct)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">% Physical Progress</span><span className="font-semibold text-cyan-700">{pc(po.physical_progress_pct)}</span></div>
              </div>
            </Panel>
          </div>

          {/* Row: Grant summary + donut + programme bars */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
            <Panel title="Grant / Scheme wise Summary (including Roads, Bridges & Buildings)" className="lg:col-span-5">
              <div className="overflow-x-auto">
                <table className="w-full text-[10px] border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th rowSpan={2} className="border px-1.5 py-1 text-left">Grant / Scheme</th>
                      <th colSpan={3} className="border px-1 py-0.5 text-center leading-tight" style={{ color: SEG.total }}>Work Load<br /><span className="text-[8px] font-normal">(Spillover + New Sanctions)</span></th>
                      <th colSpan={2} className="border px-1 py-0.5 text-center" style={{ color: SEG.completed }}>Completed</th>
                      <th colSpan={2} className="border px-1 py-0.5 text-center" style={{ color: SEG.in_progress }}>In Progress</th>
                      <th colSpan={2} className="border px-1 py-0.5 text-center" style={{ color: '#d97706' }}>Hold Up</th>
                      <th rowSpan={2} className="border px-1 py-1 text-center">Fin %</th>
                      <th rowSpan={2} className="border px-1 py-1 text-center">Phys %</th>
                    </tr>
                    <tr className="bg-gray-50 text-gray-500">
                      {['Spillover', 'New', 'Est.Cost', 'Works', 'Exp', 'Works', 'Est.Cost', 'Works', 'Est.Cost'].map((h, i) =>
                        <th key={i} className="border px-1 py-0.5 text-center font-medium">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {grants.map((g: any) => (
                      <tr key={g.grant} className="text-center">
                        <td className="border px-1.5 py-1 text-left font-medium whitespace-nowrap">{g.grant}</td>
                        <td className="border px-1 py-1">{num(g.spillover_works)}</td><td className="border px-1 py-1">{num(g.new_works)}</td><td className="border px-1 py-1">{cr(g.est_cost_cr)}</td>
                        <td className="border px-1 py-1 text-green-700">{num(g.completed)}</td><td className="border px-1 py-1 text-green-700">{cr(g.completed_exp_cr)}</td>
                        <td className="border px-1 py-1 text-orange-600">{num(g.in_progress)}</td><td className="border px-1 py-1 text-orange-600">{cr(g.in_progress_cost_cr)}</td>
                        <td className="border px-1 py-1 text-amber-700">{num(g.hold_up)}</td><td className="border px-1 py-1 text-amber-700">{cr(g.hold_up_cost_cr)}</td>
                        <td className="border px-1 py-1 font-semibold">{pc(g.financial_pct)}</td><td className="border px-1 py-1 font-semibold">{pc(g.physical_pct)}</td>
                      </tr>
                    ))}
                    {grants.length === 0 && <tr><td colSpan={12} className="border px-2 py-4 text-center text-gray-400">No data</td></tr>}
                    {grants.length > 0 && (() => {
                      const t = grants.reduce((a: any, g: any) => ({
                        spill: a.spill + g.spillover_works, nw: a.nw + g.new_works, est: a.est + g.est_cost_cr,
                        comp: a.comp + g.completed, compE: a.compE + g.completed_exp_cr,
                        ip: a.ip + g.in_progress, ipC: a.ipC + g.in_progress_cost_cr,
                        hu: a.hu + g.hold_up, huC: a.huC + g.hold_up_cost_cr,
                      }), { spill: 0, nw: 0, est: 0, comp: 0, compE: 0, ip: 0, ipC: 0, hu: 0, huC: 0 });
                      return (
                        <tr className="text-center font-bold bg-gray-50 border-t-2 border-gray-300">
                          <td className="border px-1.5 py-1 text-left text-red-700">TOTAL</td>
                          <td className="border px-1 py-1" style={{ color: SEG.total }}>{num(t.spill)}</td><td className="border px-1 py-1" style={{ color: SEG.total }}>{num(t.nw)}</td><td className="border px-1 py-1" style={{ color: SEG.total }}>{cr(t.est)}</td>
                          <td className="border px-1 py-1" style={{ color: SEG.completed }}>{num(t.comp)}</td><td className="border px-1 py-1" style={{ color: SEG.completed }}>{cr(t.compE)}</td>
                          <td className="border px-1 py-1" style={{ color: SEG.in_progress }}>{num(t.ip)}</td><td className="border px-1 py-1" style={{ color: SEG.in_progress }}>{cr(t.ipC)}</td>
                          <td className="border px-1 py-1" style={{ color: '#d97706' }}>{num(t.hu)}</td><td className="border px-1 py-1" style={{ color: '#d97706' }}>{cr(t.huC)}</td>
                          <td className="border px-1 py-1">{pc(k.financial_progress_pct)}</td><td className="border px-1 py-1">{pc(k.physical_progress_pct)}</td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </Panel>

            <Panel title="Asset-wise Break-up (All Grants)" className="lg:col-span-3">
              <div className="flex flex-col items-center justify-center h-full py-2">
                <Donut data={data.asset_breakup || []} total={k.total_works || 0} active={asset}
                  onSelect={(key) => setAsset((cur) => (cur === key ? '' : key))} />
                {asset ? (
                  <button onClick={() => setAsset('')} className="mt-2 inline-flex items-center gap-1 text-[10px] bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5 hover:bg-blue-100">
                    Filtered: {asset} <span className="font-bold">×</span>
                  </button>
                ) : (
                  <div className="mt-2 text-[9px] text-gray-400">Click a slice to filter the dashboard</div>
                )}
              </div>
            </Panel>

            <Panel title="Programme-wise Financial Progress (%)" className="lg:col-span-4">
              <div className="py-2">
                <BarList data={(data.programme_financial || []).map((p: any) => ({ label: p.scheme, value: p.financial_pct }))} color="#9e2b2b" />
                {(!data.programme_financial || data.programme_financial.length === 0) && <div className="text-center text-gray-400 text-xs py-4">No data</div>}
                <div className="text-[9px] text-gray-400 text-center mt-2">% Financial Progress</div>
              </div>
            </Panel>
          </div>

          {/* Row: stage-wise physical progress */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
            <Panel title="State Level Stage-wise Physical Progress (Nos)" className="lg:col-span-5">
              <div className="grid grid-cols-3 gap-2">
                {(['Roads', 'Bridges', 'Buildings'] as const).map((a) => {
                  const list = data.stage_physical?.[a] || [];
                  const tot = list.reduce((s: number, x: any) => s + x.count, 0);
                  return (
                    <div key={a} className="border border-gray-200 rounded overflow-hidden">
                      <div className="bg-[#2f6fb3] text-white text-[10px] font-bold px-2 py-1 text-center">{a.toUpperCase()} ({num(tot)})</div>
                      <table className="w-full text-[10px]">
                        <tbody>
                          {list.map((s: any, i: number) => (
                            <tr key={i} className="border-b border-gray-100"><td className="px-1.5 py-0.5 text-gray-600">{s.stage}</td><td className="px-1.5 py-0.5 text-right font-semibold">{num(s.count)}</td></tr>
                          ))}
                          <tr className="bg-gray-50 font-bold"><td className="px-1.5 py-0.5">Total</td><td className="px-1.5 py-0.5 text-right text-red-600">{num(tot)}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            </Panel>

            <Panel title="State Level Stage-wise Physical Progress — Summary (%)" className="lg:col-span-7">
              <div className="space-y-2 py-1">
                <StageTrack asset="Roads" icon={<Route className="h-4 w-4" />} list={data.stage_physical?.Roads || []} color="#2f6fb3" />
                <StageTrack asset="Bridges" icon={<Spline className="h-4 w-4" />} list={data.stage_physical?.Bridges || []} color="#e8852f" />
                <StageTrack asset="Buildings" icon={<Building2 className="h-4 w-4" />} list={data.stage_physical?.Buildings || []} color="#2fae5f" />
              </div>
            </Panel>
          </div>

          {/* Footer */}
          <div className="flex flex-wrap items-center justify-between text-[10px] text-gray-500 px-1 pb-2">
            <span>Note: Financial Progress = (Expenditure ÷ Estimated Cost) × 100 · Physical Progress is computed from the current stage of work.</span>
            <span className="font-semibold text-[#1f6fc4]">All Grants Combined &nbsp;|&nbsp; State Level Summary</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default StateLevelDashboard;
